/**
 * GitHub-style activity heatmap component for moderator activity visualization
 * @param {string} modId - The moderator user ID
 */
export function modActivityHeatmap(modId) {
    return {
        modId: String(modId),
        loading: true,
        error: null,
        dailyCounts: [],
        actionTotals: {},
        totalActions: 0,
        period: { start: '', end: '' },
        selectedAction: null,
        tooltipData: null,
        tooltipPosition: { x: 0, y: 0 },

        /**
         * Initialize the heatmap - called automatically by Alpine
         */
        init() {
            this.loadData();
        },

        /**
         * Fetch activity stats from the API
         */
        async loadData() {
            this.loading = true;
            this.error = null;

            try {
                const response = await window.ApiClient.request(`/moderator/${this.modId}/activity-stats`);

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                const result = await response.json();
                const data = result.data;

                this.dailyCounts = data.daily_counts || [];
                this.actionTotals = data.action_totals || {};
                this.totalActions = data.total_actions || 0;
                this.period = data.period || { start: '', end: '' };
            } catch (err) {
                this.error = 'Failed to load activity data';
                console.error('Heatmap load error:', err);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Filter by action type
         * @param {string|null} action - Action type or null for all
         */
        filterByAction(action) {
            this.selectedAction = action;
            this.loadData();
        },

        /**
         * Build a 52x7 grid data structure for the heatmap
         * Rows = days of week (Sun-Sat), Columns = weeks
         * @returns {Array<Array<{date: string, count: number, dayOfWeek: number}>>}
         */
        getGridData() {
            const endDate = this.period.end ? new Date(this.period.end) : new Date();
            const startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - 364); // 52 weeks

            // Create a map of date -> count for quick lookup
            const countMap = new Map();
            for (const item of this.dailyCounts) {
                countMap.set(item.date, item.count);
            }

            // Build weeks array (52 weeks)
            const weeks = [];
            let currentDate = new Date(startDate);

            // Adjust to start from the nearest Sunday
            const dayOffset = currentDate.getDay();
            currentDate.setDate(currentDate.getDate() - dayOffset);

            for (let week = 0; week < 53; week++) {
                const weekData = [];
                for (let day = 0; day < 7; day++) {
                    const dateStr = currentDate.toISOString().split('T')[0];
                    const count = countMap.get(dateStr) || 0;

                    weekData.push({
                        date: dateStr,
                        count: count,
                        dayOfWeek: day,
                    });

                    currentDate.setDate(currentDate.getDate() + 1);
                }
                weeks.push(weekData);
            }

            return weeks;
        },

        /**
         * Get month labels for the X-axis
         * @returns {Array<{label: string, weekIndex: number}>}
         */
        getMonthLabels() {
            const gridData = this.getGridData();
            const rawLabels = [];
            let lastMonth = -1;

            // First pass: collect all month transitions
            gridData.forEach((week, weekIndex) => {
                const firstDayOfWeek = new Date(week[0].date);
                const month = firstDayOfWeek.getMonth();

                if (month !== lastMonth) {
                    rawLabels.push({
                        label: firstDayOfWeek.toLocaleString('en-US', { month: 'short' }),
                        weekIndex: weekIndex,
                    });
                    lastMonth = month;
                }
            });

            // Second pass: remove labels that are too close, preferring later ones
            const minSpacing = 4;
            const labels = [];
            for (let i = 0; i < rawLabels.length; i++) {
                const current = rawLabels[i];
                const next = rawLabels[i + 1];

                // If next label exists and is too close, skip current (prefer newer month)
                if (next && next.weekIndex - current.weekIndex < minSpacing) {
                    continue;
                }
                labels.push(current);
            }

            return labels;
        },

        /**
         * Get Tailwind color class based on activity count
         * @param {number} count - Number of actions
         * @returns {string} Tailwind background color class
         */
        getColorClass(count) {
            if (count === 0) return 'fill-gray-100';
            if (count <= 2) return 'fill-green-200';
            if (count <= 5) return 'fill-green-400';
            if (count <= 10) return 'fill-green-600';
            return 'fill-green-800';
        },

        /**
         * Get color for inline style (for SVG)
         * @param {number} count - Number of actions
         * @returns {string} Color hex value
         */
        getColor(count) {
            const dark = document.documentElement.classList.contains('dark');
            if (dark) {
                if (count === 0) return '#1e2028'; // dark surface
                if (count <= 2) return '#1e3a5f'; // dark blue-200
                if (count <= 5) return '#2563eb'; // blue-600
                if (count <= 10) return '#3b82f6'; // blue-500
                return '#60a5fa'; // blue-400
            }
            if (count === 0) return '#f3f4f6'; // gray-100
            if (count <= 2) return '#bfdbfe'; // blue-200
            if (count <= 5) return '#60a5fa'; // blue-400
            if (count <= 10) return '#2563eb'; // blue-600
            return '#1e40af'; // blue-800
        },

        /**
         * Format date for tooltip display
         * @param {string} dateStr - Date in Y-m-d format
         * @returns {string} Formatted date string
         */
        formatDate(dateStr) {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        },

        /**
         * Show tooltip on cell hover
         * @param {Event} event - Mouse event
         * @param {Object} day - Day data object
         */
        showTooltip(event, day) {
            const rect = event.target.getBoundingClientRect();
            this.tooltipData = {
                date: this.formatDate(day.date),
                count: day.count,
            };
            this.tooltipPosition = {
                x: rect.left + window.scrollX + rect.width / 2,
                y: rect.top + window.scrollY - 8,
            };
        },

        /**
         * Hide tooltip
         */
        hideTooltip() {
            this.tooltipData = null;
        },

        /**
         * Get action label for display
         * @param {string} action - Action type
         * @returns {string} Human-readable label
         */
        getActionLabel(action) {
            const labels = {
                'delete': 'Deletions',
                'approve': 'Approvals',
                'warn': 'Warnings',
                'restore': 'Restorations',
                'log': 'Log Notes',
                'shadowban': 'Shadowbans',
                'escalate': 'Escalations',
                'reinstate': 'Reinstatements',
                'photo-delete': 'Photo Deletions',
                'photo-approve': 'Photo Approvals',
            };
            return labels[action] || action;
        },

        /**
         * Get badge color class for action type
         * @param {string} action - Action type
         * @returns {string} Tailwind classes for badge
         */
        getActionBadgeClass(action) {
            const classes = {
                'delete': 'bg-red-100 text-red-800',
                'approve': 'bg-green-100 text-green-800',
                'warn': 'bg-yellow-100 text-yellow-800',
                'restore': 'bg-blue-100 text-blue-800',
                'log': 'bg-gray-100 text-gray-800',
                'shadowban': 'bg-purple-100 text-purple-800',
                'escalate': 'bg-orange-100 text-orange-800',
                'reinstate': 'bg-teal-100 text-teal-800',
                'photo-delete': 'bg-red-100 text-red-800',
                'photo-approve': 'bg-green-100 text-green-800',
            };
            return classes[action] || 'bg-gray-100 text-gray-800';
        },

        /**
         * Get text color class for action type (for outline buttons)
         * @param {string} action - Action type
         * @returns {string} Tailwind text color class
         */
        getActionTextClass(action) {
            const classes = {
                'delete': 'text-red-600',
                'approve': 'text-green-600',
                'warn': 'text-yellow-600',
                'restore': 'text-blue-600',
                'log': 'text-gray-600',
                'shadowban': 'text-purple-600',
                'escalate': 'text-orange-600',
                'reinstate': 'text-teal-600',
                'photo-delete': 'text-red-600',
                'photo-approve': 'text-green-600',
            };
            return classes[action] || 'text-gray-600';
        },
    };
}
